import React, { useRef, useState } from "react";
import { CONSTANTS } from "../../constants/Constants";
import playIcon from "../../assets/icons/play_icon.svg";
import pauseIcon from "../../assets/icons/pause_icon.svg";
import fullScreen from "../../assets/icons/full-screen-icon.png";
import LoadSpinner from "../LoadingSpinner";
import { useGlobalContext } from "../../context/GlobalContext";

interface VideoCardProp {
  name?: string;
  description?: string;
  mediaUrl: string;
}

const VideoCard: React.FC<VideoCardProp> = ({
  name,
  description,
  mediaUrl,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const {isLoading, setIsLoading} = useGlobalContext()
  const [isPlaying, setIsPlaying] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleModalToggle = () => {
    setIsModalOpen(!isModalOpen);
  };

  const isYouTubeUrl = (url: string) => {
    const youTubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be)/;
    return youTubeRegex.test(url);
  };

  const extractYouTubeId = (url: string) => {
    const regex =
      /(?:youtube\.com\/(?:[^\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  return (
    <div className="relative w-[300px]">
      {/* Video Element */}
      {isYouTubeUrl(mediaUrl) ? (
        // Render YouTube video in an iframe
        <iframe
          className="w-[300px] h-full"
          src={`https://www.youtube.com/embed/${extractYouTubeId(mediaUrl)}`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      ) : (
        <>
          <div className="relative h-[190px] md:h-[170px] overflow-hidden w-[300px] max-w-md">
            {/* Video Element */}
            <div
              className={`${
                isModalOpen
                  ? "fixed top-0 left-0 w-full h-[100%] z-50 bg-black"
                  : "relative h-[100%] w-[100%]"
              }`}
            >
              <video
                ref={videoRef}
                className={`${isModalOpen ? "w-full h-full" : "w-[100%] h-[100%"}`}
                controls={false}
                onCanPlay={() => setIsLoading(false)}
                onEnded={togglePlayPause}
              >
                <source src={mediaUrl} type="video/mp4" />
                {CONSTANTS.VIDEO_SUPPORT_TXT}
              </video>
              {isLoading ? (
                <div className="relative w-full flex items-center justify-center">
                  <LoadSpinner classes="absolute bottom-14" />
                </div>
              ) : (
                <div className="flex justify-between items-end absolute bottom-0 w-full bg-gradient-to-t from-black to-transparent p-4 text-white">
                  <div>
                    <h2 className="text-sm">{name}</h2>
                    <p className="text-[11px] mt-1">{description}</p>
                  </div>
                  <div className="flex gap-2">
                    <div
                      onClick={togglePlayPause}
                      className="flex justify-center items-center bg-white bg-opacity-50 rounded-full w-8 h-8 cursor-pointer"
                    >
                      <img
                        src={isPlaying ? pauseIcon : playIcon}
                        className="w-fit"
                        alt="Play/Pause Icon"
                      />
                    </div>
                    <div
                      onClick={handleModalToggle}
                      className="flex justify-center items-center bg-white bg-opacity-50 rounded-full w-8 h-8 cursor-pointer"
                    >
                      <img
                        src={fullScreen}
                        className="w-[15px]"
                        alt="Fullscreen Icon"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VideoCard;
